import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ContentHeader from '../components/ContentHeader'
import EmptyState from '../components/EmptyState'
import LoadingPlaceholder from '../components/LoadingPlaceholder'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import { listClients, archiveClient } from '../api/crm'
import '../style/views/ContactsView.css'

const sortFilters: string[] = ['First Name', 'Last Name', 'Company']
const CONTACTS_SORT_STORAGE_KEY = 'contactsSortBy'
const isContactsSort = (value: string | null): value is (typeof sortFilters)[number] =>
  value === 'First Name' || value === 'Last Name' || value === 'Company'

type Client = {
  id: string
  first_name?: string
  last_name?: string
  email?: string
  cell?: string
  company?: string
  notes?: string
}

function ContactsView() {
  const navigate = useNavigate()
  const [sortBy, setSortBy] = useState<(typeof sortFilters)[number]>(() => {
    if (typeof window === 'undefined') return sortFilters[0]
    const stored = window.localStorage.getItem(CONTACTS_SORT_STORAGE_KEY)
    return isContactsSort(stored) ? stored : sortFilters[0]
  })
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [sorting, setSorting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [archivingSelected, setArchivingSelected] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const loadClients = async () => {
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) {
        setLoading(false)
        return
      }
      if (!userId) setUserId(ensuredId)
      try {
        setLoading(true)
        setLoadError(null)
        const data = await listClients(ensuredId, controller.signal)
        setClients(data)
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load clients', err)
          setClients([])
          setLoadError('Unable to load contacts.')
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    loadClients()
    return () => controller.abort()
  }, [userId])

  const filteredClients = useMemo(() => {
    const sortKey =
      sortBy === 'First Name' ? 'first_name' : sortBy === 'Last Name' ? 'last_name' : 'company'
    return clients.toSorted((a, b) => (a[sortKey] || '').localeCompare(b[sortKey] || ''))
  }, [clients, sortBy])

  useEffect(() => {
    setSorting(true)
    const t = setTimeout(() => setSorting(false), 260)
    return () => clearTimeout(t)
  }, [sortBy])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(CONTACTS_SORT_STORAGE_KEY, sortBy)
  }, [sortBy])

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handleArchiveSelected = useCallback(async () => {
    if (!selectedIds.size || archivingSelected) return
    const confirmed = window.confirm(
      `Archive ${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'}?`,
    )
    if (!confirmed) return
    try {
      setArchivingSelected(true)
      setArchiveError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      const archiveResults = await Promise.all(
        Array.from(selectedIds).map(async (id) => {
          try {
            await archiveClient(id, ensuredId)
            return { id, ok: true }
          } catch (error) {
            console.error('Failed to archive contact', error)
            return { id, ok: false }
          }
        }),
      )
      const archivedIds = new Set(archiveResults.flatMap((r) => r.ok ? [r.id] : []))
      const failedIds = archiveResults.flatMap((r) => r.ok ? [] : [r.id])
      if (archivedIds.size) {
        setClients((prev) => prev.filter((client) => !archivedIds.has(client.id)))
        setSelectedIds((prev) => {
          const next = new Set(prev)
          archivedIds.forEach((id) => next.delete(id))
          return next
        })
      }
      if (failedIds.length) {
        setArchiveError(`Unable to archive ${failedIds.length} contact${failedIds.length === 1 ? '' : 's'}.`)
      }
    } catch (error) {
      console.error('Failed to archive selected contacts', error)
      setArchiveError(error instanceof Error ? error.message : 'Unable to archive selected contacts.')
    } finally {
      setArchivingSelected(false)
    }
  }, [archivingSelected, selectedIds, userId])

  const handleMergeSelected = useCallback(() => {
    if (selectedIds.size < 2) return
    const idList = Array.from(selectedIds).join(',')
    navigate(`/contacts/merge?ids=${encodeURIComponent(idList)}`)
  }, [selectedIds, navigate])

  return (
    <>
      <ContentHeader
        title="Contacts"
        filters={sortFilters}
        activeFilter={sortBy}
        onFilterChange={(value) => setSortBy(value)}
        actions={
          selectedIds.size ? (
            <>
              {selectedIds.size > 1 && (
                <button
                  type="button"
                  className="signal-button header-action-button"
                  onClick={handleMergeSelected}
                >
                  Merge ({selectedIds.size})
                </button>
              )}
              <button
                type="button"
                className="danger-button header-action-button"
                onClick={handleArchiveSelected}
                disabled={archivingSelected}
              >
                {archivingSelected ? 'Archiving...' : `Archive selected (${selectedIds.size})`}
              </button>
              <button type="button" className="ghost-button header-action-button" onClick={handleClearSelection}>
                Cancel
              </button>
            </>
          ) : null
        }
      />
      <div
        className="content-panel"
        onContextMenu={(e) => {
          e.preventDefault()
          setContextMenu({ x: e.clientX, y: e.clientY + window.scrollY })
        }}
        onMouseDown={() => setContextMenu(null)}
      >
        {archiveError && <div className="muted archive-error" style={{ color: '#b42318' }}>{archiveError}</div>}
        {loading ? (
          <LoadingPlaceholder variant={'all'} />
        ) : loadError ? (
          <div data-testid="contacts-error" className="muted archive-error">{loadError}</div>
        ) : filteredClients.length === 0 ? (
          <div data-testid="contacts-empty"><EmptyState /></div>
        ) : (
          <div data-testid="contacts-list" className={`tiles-grid contacts-grid ${sorting ? 'sorting' : ''}`}>
            {filteredClients.map((client, index) => {
              const name =
                [client.first_name, client.last_name].filter(Boolean).join(' ').trim() || 'Unknown Client'
              const isSelected = selectedIds.has(client.id)
              return (
                <div
                  key={client.id}
                  className={`tile data-tile clickable sort-animate${isSelected ? ' selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  style={{ animationDelay: sorting ? `${index * 25}ms` : undefined }}
                  onClick={() => navigate(`/contact/${client.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/contact/${client.id}`)
                    }
                  }}
                >
                  <button
                    type="button"
                    className={`tile-select ${isSelected ? 'selected' : ''}`}
                    aria-label={isSelected ? 'Deselect contact' : 'Select contact'}
                    aria-pressed={isSelected}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleSelect(client.id)
                    }}
                  >
                    {isSelected ? '✓' : ''}
                  </button>
                  <div className="tile-meta">
                    {client.company && <span className="tile-pill">{client.company}</span>}
                    {client.cell && <span className="tile-pill muted">{client.cell}</span>}
                    {client.email && <span className="tile-pill muted">{client.email}</span>}
                  </div>
                  <div className="tile-title" title={name}>
                    {name}
                  </div>
                  {client.notes && <p className="muted" style={{ marginTop: '6px' }}>{client.notes}</p>}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {contextMenu && (
        <div
          style={{
            position: 'absolute',
            top: contextMenu.y,
            left: contextMenu.x,
          }}
          className="context-menu"
        >
          <button
            className="context-menu-item"
            onClick={() => {
              setContextMenu(null)
              navigate('/contact/new')
            }}
          >
            Create new contact
          </button>
        </div>
      )}
    </>
  )
}

export default ContactsView
